import { useAppDispatch } from '@/lib/redux/store';
import { ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { addToast, removeToast } from '../store/toastSlice';
import { AlertType, ToastConfig, ToastOptions } from '../types';

const DEFAULT_DURATION = 10000; // 10 seconds

interface HtmlToastOptions extends ToastOptions {
  hideIcon?: boolean;
}

const useToast = () => {
  const dispatch = useAppDispatch();

  const createToast = (
    alertType: AlertType,
    content: ToastConfig['content'],
    options?: ToastOptions
  ) => {
    const id = uuidv4();
    const toast: ToastConfig = {
      id,
      alertType,
      content,
      ...options,
    };

    dispatch(addToast(toast));

    if (!options?.isManualDismiss) {
      setTimeout(() => dispatch(removeToast(id)), options?.duration ?? DEFAULT_DURATION);
    }
  };

  return {
    // Simple text methods
    showError: (message: string, options?: ToastOptions) =>
      createToast('error', { type: 'text', message }, options),
    showSuccess: (message: string, options?: ToastOptions) =>
      createToast('success', { type: 'text', message }, options),
    showWarning: (message: string, options?: ToastOptions) =>
      createToast('warning', { type: 'text', message }, options),
    showInfo: (message: string, options?: ToastOptions) =>
      createToast('info', { type: 'text', message }, options),

    // HTML content methods
    showHtmlError: (content: ReactNode, options?: HtmlToastOptions) =>
      createToast(
        'error',
        { type: 'html', content, hideIcon: options?.hideIcon },
        options
      ),
    showHtmlSuccess: (content: ReactNode, options?: HtmlToastOptions) =>
      createToast(
        'success',
        { type: 'html', content, hideIcon: options?.hideIcon },
        options
      ),
    showHtmlWarning: (content: ReactNode, options?: HtmlToastOptions) =>
      createToast(
        'warning',
        { type: 'html', content, hideIcon: options?.hideIcon },
        options
      ),
    showHtmlInfo: (content: ReactNode, options?: HtmlToastOptions) =>
      createToast(
        'info',
        { type: 'html', content, hideIcon: options?.hideIcon },
        options
      ),

    // Generic method
    showToast: (config: Omit<ToastConfig, 'id'>) =>
      createToast(config.alertType, config.content, {
        duration: config.duration,
        isManualDismiss: config.isManualDismiss,
      }),
  };
};

export default useToast;
